import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { NotionRenderer } from 'react-notion';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import SystemProvider from 'components/system/SystemProvider';
import QuantumCountdown from 'components/system/QuantumCoundown';
import {
  generateHeadingId, removeAllSelect, goTo, filterObjectByRole,
} from 'utils/docs';
// import { createQuantumWindowsMessage } from 'utils/time';
import usePermissions from 'hooks/usePermissions';
import Prism from 'prismjs';
import withSeo from 'components/hoc/withSeo';
import ActionableBanner from '../ActionableBanner';
import SampleJobButtons from './SampleJobButtons';
import DevicesTable from './DevicesTable';
import notionDataLocal from './Notion/docs.notion.json';
import 'prismjs/themes/prism-tomorrow.css';
import 'styles/notion.css';

const cleanupButtonContainer = (buttonIdentifier) => {
  const paragraphs = document.querySelectorAll('p') || [];

  const sampleJobButtonContainer = Array
    .from(paragraphs)
    .find(((el) => el.innerText === buttonIdentifier));
  sampleJobButtonContainer.innerText = '';
  sampleJobButtonContainer.id = buttonIdentifier;
};

const insertTimeRange = () => {
  const paragraphs = document.querySelectorAll('.notion-callout-text');
  const timeWindowContainer = Array
    .from(paragraphs)
    .find(((el) => el.innerText.startsWith('magicWindow')));

  if (timeWindowContainer) {
    timeWindowContainer.innerText = '';

    const timeWindowRoot = createRoot(timeWindowContainer);

    timeWindowRoot.render(
      (
        <SystemProvider>
          <QuantumCountdown />
          {' '}
          <p className="monospace">The quantum device is available 24/7:</p>
          <p className="monospace">Mon-Sun: 12:00 AM - 11:59 PM (All day)</p>
          {/* <p className="monospace">The quantum device is only available during these times:</p> */}
          {/* <div> */}
          {/*  {createQuantumWindowsMessage() */}
          {/*    .map((message) => <p key={message} className="monospace">{message}</p>)} */}
          {/* </div> */}
        </SystemProvider>
      ),
    );
  }
};

const DocsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [contentRendered, setContentRendered] = useState(false);
  const [notionData, setNotionData] = useState({});
  const notionRef = useRef(null);
  const [headings, setHeadings] = useState([]);

  const { hasPermission } = usePermissions();

  useEffect(() => {
    setNotionData(filterObjectByRole(notionDataLocal, 'none'));
    setContentRendered(true);
    return () => {};
  }, []);

  useEffect(() => {
    if (contentRendered) {
      insertTimeRange();
      cleanupButtonContainer('magicButtonA_plus_B');
      cleanupButtonContainer('magicButtonQuantum');
      cleanupButtonContainer('magicDevices_Table');
      const notion = document.querySelectorAll('.notion')[0];
      Prism.highlightAllUnder(notion, false);
    }
  }, [contentRendered]);

  useEffect(() => {
    if (location.hash && headings.length > 0) {
      const paragraphId = location.hash.slice(1);
      const selectedHeading = headings.find((heading) => heading.id === paragraphId);
      if (selectedHeading) {
        goTo(selectedHeading);
        removeAllSelect();
        const menuHeader = document.querySelector(`[data-id="${paragraphId}"]`);
        if (menuHeader) {
          menuHeader.className += ' menu-selected';
        }
      }
    }
  }, [location, headings]);

  useEffect(() => {
    const selectedHeadings = Array.from(notionRef
      .current
      .querySelectorAll('.notion-h1, .notion-h2, .notion-h3, .notion-h4'));

    selectedHeadings.forEach((selectedHeading) => {
      selectedHeading.setAttribute('id', generateHeadingId(selectedHeading.innerText));
    });

    setHeadings(selectedHeadings);
  }, [contentRendered]);

  const navigationData = [];

  for (const heading of headings) {
    const className = `table-content-${heading?.className}`;
    const menuId = generateHeadingId(`${heading.innerText}`);
    navigationData
      .push(
        <div
          key={menuId}
          data-id={menuId}
          className={className}
          role="button"
          tabIndex="0"
          onClick={() => {
            goTo(heading);
            navigate(`#${menuId}`);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              goTo(heading);
              navigate(`#${menuId}`);
            }
          }}
        >
          {heading.textContent}
        </div>,
      );
  }

  return (
    <Grid container>
      <Grid item xl={2} lg={3} xs={4}>
        <div className="navigation">
          {navigationData}
        </div>
      </Grid>
      <Grid ref={notionRef} item xl={10} lg={9} xs={8}>
        <Box sx={{ padding: '40px 40px 0 40px' }}>
          {hasPermission('promo.banner') && <ActionableBanner />}
        </Box>
        <NotionRenderer blockMap={notionData} />
      </Grid>
      <SampleJobButtons />
      <DevicesTable />
    </Grid>
  );
};

export default withSeo(DocsPage);
